customElements.define('product-form', class ProductForm extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('form');
    if (this.form) {
      this.form.addEventListener('submit', (e) => e.preventDefault());
      this.form.addEventListener('click', (e) => {
        e.stopImmediatePropagation();

        const submitBtn = e.target.closest('button[type="submit"][name="add"], button[type="submit"].product-form__submit, button[type="submit"].product-atc-btn');
        if (submitBtn && !submitBtn.disabled) {
          e.preventDefault();
          this.onSubmitHandler(submitBtn);
        }
      });
    }

    this.cartNotification = document.querySelector('cart-notification');
  }

  get cartDrawer() {
    return document.querySelector('cart-drawer');
  }

  onSubmitHandler(submitButton) {
    if (!submitButton || !this.form) return;

    // Preorder gate "Pause for everyone (no companion)": never add a paused
    // product, even before Stoq converts the button into its paused state.
    if (typeof window.isPreorderVariantPaused === 'function' &&
        window.isPreorderVariantPaused(new FormData(this.form).get('id'))) return;

    submitButton.setAttribute('disabled', true);
    submitButton.classList.add('loading');

    // Remember the label so the finally block can put back whatever was there
    // (e.g. "Pre-Order" from the preorder_button metafield on stroller PDPs,
    // which have no swatch inputs to recompute it from).
    const originalLabel = submitButton.querySelector('span')?.textContent;

    const formFields = JSON.parse(serializeForm(this.form));

    // Custom enforcement of the Stoq preorder unit cap (the app itself only
    // reflects it on page reload): never let the cart hold more than
    // _StoqConfig.variantsPreorderMaxCount of this variant. Uncapped variants
    // (no app config / NaN) are unaffected.
    const capLimit = typeof window.getStoqPreorderLimit === 'function'
      ? window.getStoqPreorderLimit(formFields.id)
      : null;
    if (!capLimit) {
      this.submitToCart(submitButton, originalLabel, formFields);
      return;
    }

    fetch('/cart.js')
      .then((response) => response.json())
      .then((cart) => {
        const inCart = cart.items.find((item) => String(item.variant_id) === String(formFields.id));
        const remaining = capLimit - (inCart ? inCart.quantity : 0);

        if (remaining <= 0) {
          // The main line is at its cap: skip re-adding it, but the checked
          // e-wheels upsell and companions are SEPARATE lines with their own
          // caps — still add whatever of those is missing instead of
          // silently dropping them with the main product.
          const extraItems = this.buildExtraItems(formFields.id, cart);
          const finish = () => {
            submitButton.classList.remove('loading');
            submitButton.removeAttribute('disabled');
            if (typeof window.syncStoqPreorderCapState === 'function') {
              window.syncStoqPreorderCapState();
            }
            if (this.cartDrawer) this.cartDrawer.open();
          };
          if (extraItems.length === 0) {
            finish();
            return;
          }
          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ items: extraItems })
          })
            .then((res) => res.json())
            .then(() => {
              return fetch(`${routes.cart_update_url}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify({
                  updates: {},
                  sections: this.getSectionsToRender().map((section) => section.section),
                  sections_url: window.location.pathname
                })
              }).then((response) => response.json());
            })
            .then((parsedState) => {
              this.getSectionsToRender().forEach((section) => {
                const elementToReplace =
                  document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
                elementToReplace.innerHTML =
                  this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
              });
            })
            .catch((e) => {
              console.error(e);
            })
            .finally(finish);
          return;
        }

        const requested = parseInt(formFields.quantity, 10) || 1;
        if (requested > remaining) formFields.quantity = String(remaining);
        this.submitToCart(submitButton, originalLabel, formFields);
      })
      .catch(() => {
        // A failed cap lookup must never block purchases.
        this.submitToCart(submitButton, originalLabel, formFields);
      });
  }

  /**
   * Builds the extra cart items that ride along with the main add: the PDP
   * free-gift / preorder-gate companion (data-free-gift) and the checked
   * e-wheels upsell (+ ITS companion when the upsell is a gated product).
   * Every item is clamped against its own quantity cap (customizer limit
   * and/or Stoq preorder limit, via getStoqPreorderLimit) minus what the
   * cart already holds, and companions already parented in the cart are not
   * duplicated — so this is safe to call on repeat adds and when the main
   * product itself was skipped for being at ITS cap.
   */
  buildExtraItems(parentVariantId, cart) {
    const extraItems = [];
    const gateCfg = window.preorderGateConfig;
    const cartItems = (cart && cart.items) || [];

    const inCartQty = (variantId) => cartItems
      .filter((item) => String(item.variant_id) === String(variantId))
      .reduce((sum, item) => sum + item.quantity, 0);
    const clampQty = (variantId, quantity) => {
      const cap = typeof window.getStoqPreorderLimit === 'function'
        ? window.getStoqPreorderLimit(variantId)
        : null;
      return cap ? Math.min(quantity, Math.max(cap - inCartQty(variantId), 0)) : quantity;
    };
    // Variant-specific: a gated parent can hold BOTH the main companion and
    // the metafield (e-wheels) upsell as _free_gift lines, so "any companion
    // under this parent" would wrongly skip the other one on re-adds.
    const hasCompanionParentedTo = (parentId, companionVariantId) => cartItems.some((item) =>
      String(item.variant_id) === String(companionVariantId) &&
      item.properties && item.properties._free_gift === 'true' &&
      String(item.properties._parent_variant_id) === String(parentId));
    // Companion on Stoq preorder: attach its selling plan so the line is
    // added in its preorder state, not as a plain default variant, plus the
    // app's line properties (visible shipping text in cart).
    const buildCompanionItem = (companionId, quantity, parentId) => {
      const item = {
        id: companionId,
        quantity: quantity,
        properties: {
          _free_gift: 'true',
          _parent_variant_id: String(parentId)
        }
      };
      if (gateCfg && gateCfg.companionSellingPlanId &&
          String(gateCfg.companionVariantId) === String(companionId)) {
        item.selling_plan = gateCfg.companionSellingPlanId;
        if (typeof window.getPreorderCompanionProperties === 'function') {
          Object.assign(item.properties, window.getPreorderCompanionProperties(companionId));
        }
      }
      return item;
    };

    const freeGiftId = this.dataset.freeGift;
    if (freeGiftId && freeGiftId !== '' && parentVariantId && !hasCompanionParentedTo(parentVariantId, freeGiftId)) {
      const freeGiftQty = clampQty(freeGiftId, 1);
      if (freeGiftQty > 0) {
        extraItems.push(buildCompanionItem(freeGiftId, freeGiftQty, parentVariantId));
      }
    }

    // E-Wheels / metafield upsell checkbox (stroller PDPs): add the
    // equivalent variant alongside the wagon, matching the wagon quantity.
    // Variant id comes from data-add-variant-id (e_wheels_upgrade_product).
    // On gated preorder PDPs the add is PARENTED like the companion
    // (_free_gift / _parent_variant_id) so removing the wagon removes it.
    // Regular (non-gated) wagons still get a standalone e-wheels line.
    const eWheelsCheckbox = this.querySelector('#add-e-wheels-package');
    const eWheelsVariantId = eWheelsCheckbox?.dataset.addVariantId;
    const parentIsGated = !!(gateCfg && (
      gateCfg.gateActive ||
      (Array.isArray(gateCfg.gatedVariantIds) && gateCfg.gatedVariantIds.includes(String(parentVariantId)))
    ));
    const eWheelsPaused = typeof window.isPreorderVariantPaused === 'function' &&
      window.isPreorderVariantPaused(eWheelsVariantId);
    if (eWheelsCheckbox?.checked && !eWheelsCheckbox.disabled && !eWheelsPaused && eWheelsVariantId && parentVariantId) {
      const alreadyParented = hasCompanionParentedTo(parentVariantId, eWheelsVariantId);
      const mainQuantity = parseInt(new FormData(this.form).get('quantity'), 10) || 1;
      const eWheelsQty = clampQty(eWheelsVariantId, mainQuantity);
      if (eWheelsQty > 0 && !alreadyParented) {
        const eWheelsItem = {
          id: eWheelsVariantId,
          quantity: eWheelsQty
        };
        // Upsell on Stoq preorder: carry its selling plan + the app's line
        // properties (visible shipping text) so it lands as a preorder line.
        const eWheelsPlan = typeof window.getStoqSellingPlanFor === 'function'
          ? window.getStoqSellingPlanFor(eWheelsVariantId)
          : null;
        if (parentIsGated) {
          eWheelsItem.properties = {
            _free_gift: 'true',
            _parent_variant_id: String(parentVariantId)
          };
          if (eWheelsPlan) {
            eWheelsItem.selling_plan = eWheelsPlan.shopify_selling_plan_id;
            Object.assign(eWheelsItem.properties, window.getStoqPreorderLineProperties(eWheelsVariantId));
          }
        } else if (eWheelsPlan) {
          eWheelsItem.selling_plan = eWheelsPlan.shopify_selling_plan_id;
          eWheelsItem.properties = window.getStoqPreorderLineProperties(eWheelsVariantId);
        }
        extraItems.push(eWheelsItem);
      }

      // The upsell product may itself be a gated preorder product — bring
      // its companion along, same as adding it from its own PDP. Skip this
      // when the upsell is already a gift of the gated wagon (the wagon
      // already carries the main companion; parenting a second companion
      // to the upsell would orphan it when the wagon is removed).
      if (!parentIsGated && gateCfg && gateCfg.companionVariantId && Array.isArray(gateCfg.gatedVariantIds) &&
          gateCfg.gatedVariantIds.includes(String(eWheelsVariantId)) &&
          !hasCompanionParentedTo(eWheelsVariantId, gateCfg.companionVariantId)) {
        const companionQty = clampQty(gateCfg.companionVariantId, 1);
        if (companionQty > 0) {
          extraItems.push(buildCompanionItem(gateCfg.companionVariantId, companionQty, eWheelsVariantId));
        }
      }
    }

    return extraItems;
  }

  /**
   * Companion lines already in the cart must FOLLOW the parent's quantity
   * when it grows via a PDP re-add (buildExtraItems skips existing
   * companions, so without this the parent goes to 2 while the companion
   * stays at 1). Returns [{key, quantity}] line-key changes, each clamped by
   * the companion's own cap. Keys, not indexes: /cart/change.js reorders the
   * cart.
   */
  buildCompanionQtySyncs(parentVariantId, cart) {
    const cartItems = (cart && cart.items) || [];
    const parentQty = cartItems
      .filter((item) => String(item.variant_id) === String(parentVariantId))
      .reduce((sum, item) => sum + item.quantity, 0);
    if (!parentQty) return [];

    const syncs = [];
    cartItems.forEach((item) => {
      if (!item.properties || item.properties._free_gift !== 'true' ||
          String(item.properties._parent_variant_id) !== String(parentVariantId)) return;
      const cap = typeof window.getStoqPreorderLimit === 'function'
        ? window.getStoqPreorderLimit(item.variant_id)
        : null;
      const target = cap ? Math.min(parentQty, cap) : parentQty;
      if (target !== item.quantity) {
        syncs.push({ key: item.key, quantity: target });
      }
    });
    return syncs;
  }

  submitToCart(submitButton, originalLabel, formFields) {
    const body = JSON.stringify({
      ...formFields,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname
    });

    fetch(`${routes.cart_add_url}`, { ...fetchConfig('javascript'), body })
      .then((response) => response.json())
      .then((parsedState) => {
        const addedItem = parsedState.items ? parsedState.items[parsedState.items.length - 1] : parsedState;
        if (!addedItem || !addedItem.variant_id) return parsedState;

        // Extras (companion / e-wheels upsell) are clamped against their own
        // caps AND what's already in the cart, so fetch the live cart first.
        // Existing companion lines are quantity-synced to the parent's new
        // total (a re-add grows the parent line; the companion must follow).
        return fetch('/cart.js')
          .then((res) => res.json())
          .catch(() => null)
          .then((cart) => {
            const extraItems = this.buildExtraItems(addedItem.variant_id, cart);
            const companionSyncs = this.buildCompanionQtySyncs(addedItem.variant_id, cart);
            if (extraItems.length === 0 && companionSyncs.length === 0) return parsedState;

            const runSyncs = companionSyncs.reduce((chain, sync) => chain.then(() =>
              fetch(`${routes.cart_change_url}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify({ id: sync.key, quantity: sync.quantity })
              })
            ), Promise.resolve());

            return runSyncs
              .then(() => {
                if (extraItems.length === 0) return null;
                return fetch('/cart/add.js', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                  },
                  body: JSON.stringify({ items: extraItems })
                }).then(res => res.json());
              })
              .then(() => {
                return fetch(`${routes.cart_update_url}`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                  },
                  body: JSON.stringify({
                    updates: {},
                    sections: this.getSectionsToRender().map((section) => section.section),
                    sections_url: window.location.pathname
                  })
                }).then(response => response.json());
              });
          });
      })
      .then((parsedState) => {
        this.getSectionsToRender().forEach((section => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);

          elementToReplace.innerHTML =
            this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);

        }));
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        submitButton.classList.remove('loading');
        submitButton.removeAttribute('disabled');
        const isOutOfStock =
          typeof window.isMainAtcOutOfStock === 'function' &&
          window.isMainAtcOutOfStock(submitButton);
        if (!isOutOfStock) {
          // Stoq/Restock Rocket owns the label on buttons it converted to
          // preorder ("Preorder") - restoring "Add to Cart" here would
          // clobber it after the first add.
          const isStoqPreorder = submitButton.classList.contains('restock-rocket-preorder-button');
          const span = submitButton.querySelector('span');
          if (span && !isStoqPreorder) {
            const checkedOption = this.form.querySelector(
              '.Swatches_Box input:checked, .product-form__dropdown-container input:checked'
            );
            if (checkedOption?.dataset?.preorder === 'true') {
              span.textContent = 'Pre-Order';
            } else if (checkedOption) {
              span.textContent = window.variantStrings?.addToCart || 'Add to Cart';
            } else if (originalLabel) {
              // No swatch inputs on this PDP (e.g. stroller sections):
              // restore the server-rendered label instead of assuming ATC.
              span.textContent = originalLabel;
            }
          }
          submitButton.style.display = '';
        }
        // Stoq only reflects its preorder unit cap on reload — re-check now so
        // a capped variant flips to "Out of Stock" immediately after this add.
        if (typeof window.syncStoqPreorderCapState === 'function') {
          window.syncStoqPreorderCapState();
        }
        if (this.cartDrawer) {
          this.cartDrawer.open();
        }
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer__content',
        section: document.getElementById('cart-drawer__content').dataset.id,
        selector: '.cart-drawer__content',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }
});